import { createColumnHelper } from '@tanstack/react-table';
import Decimal from 'decimal.js-light';

import { shortFormatter } from '@/lib/formatting';

import { fontStyles } from '@/styles/typography';

import { SubscriptNotation } from '@/ui/SubscriptNotation';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import {
  Asset,
  DiscoveryPreset,
  DiscoveryTimeframe,
  DiscoveryTokenResult,
  isNeonDbTokensBackedPreset,
  isAsset,
} from '@/utils/discovery/types';
import { tokenKey } from '@/utils/normalizeAddress';

import { HeaderCell } from '../Cells/HeaderCell';
import { HoldersCell } from '../Cells/HoldersCell';
import { IssuerCell } from '../Cells/IssuerCell';
import { LiquidityScoreCell } from '../Cells/LiquidityScoreCell';
import { PriceChangeCell } from '../Cells/PriceChangeCell';
import { TokenNameCell } from '../Cells/TokenNameCell';
import { UnifiedLiquidityScoreCell } from '../Cells/UnifiedLiquidityScoreCell';
import { UnifiedTokenNameCell } from '../Cells/UnifiedTokenNameCell';

type DiscoveryTableMeta = {
  preset: DiscoveryPreset | undefined;
  timeframe: DiscoveryTimeframe;
  liquidityScores: Map<string, number | undefined>;
  isLiquidityScoresLoading: boolean;
  tokenResultsMap: Map<string, TokenResult>;
  formatFiat: (
    amount: number | string,
    showExtraSmallAmounts?: boolean,
    showDecimals?: boolean,
  ) => string;
  currencySymbol: string;
  convertFromUsd: (usdAmount: number) => number;
};

// Mixed type for rows that can be either assets or standalone tokens
// TODO: When API is ready, this can be removed if API returns uniform types
// (either all Asset[] with single-token assets converted to DiscoveryTokenResult, or all Asset[])
type MixedTokenRow = Asset | DiscoveryTokenResult;

const columnHelper = createColumnHelper<DiscoveryTokenResult>();
const unifiedColumnHelper = createColumnHelper<MixedTokenRow>();

const tokenColumn = columnHelper.accessor('token', {
  size: 320,
  header: 'Token',
  id: 'token',
  cell: ({ row }) => <TokenNameCell item={row.original} />,
});

const priceColumn = columnHelper.accessor('price', {
  size: 150,
  header: (props) => <HeaderCell title="Price" property="price" sortable={true} {...props} />,
  cell: ({ row, table }) => {
    const { currencySymbol, convertFromUsd } = table.options.meta as DiscoveryTableMeta;
    return (
      <SubscriptNotation
        className={fontStyles({ variant: 'BODY_MD' })}
        number={new Decimal(convertFromUsd(parseFloat(row.original.price)))}
        symbol={currencySymbol}
      />
    );
  },
});

const priceChangeColumn = (timeframe: '5m' | '1h' | '4h' | '12h' | '24h' | '7d' | '30d') =>
  columnHelper.accessor(`change${timeframe}`, {
    size: 100,
    header: (props) => (
      <HeaderCell
        title={`${timeframe} %`}
        property={`change${timeframe}`}
        sortable={true}
        {...props}
      />
    ),
    cell: ({ row }) => <PriceChangeCell priceChange={row.original[`change${timeframe}`]} />,
  });

const marketCapColumn = columnHelper.accessor('marketCap', {
  size: 150,
  header: (props) => (
    <HeaderCell title="Market Cap" property="marketCap" sortable={true} {...props} />
  ),
  cell: ({ row, table }) => {
    const marketCap = row.original.marketCap;
    if (marketCap === null || marketCap === undefined || marketCap === '0' || marketCap === '') {
      return '-';
    }
    const { formatFiat } = table.options.meta as DiscoveryTableMeta;
    return formatFiat(marketCap, false, false);
  },
});

function formatTimeframeLabel(timeframe: string): string {
  return timeframe.includes('m') ? timeframe : `${timeframe}h`;
}

const volumeByTimeColumn = columnHelper.accessor('volByTime', {
  size: 120,
  header: (props) => {
    const { timeframe } = props.table.options.meta as DiscoveryTableMeta;
    const title = `Volume (${formatTimeframeLabel(timeframe)})`;

    return <HeaderCell title={title} property="volByTime" sortable={true} {...props} />;
  },
  cell: ({ row, table }) => {
    const { formatFiat } = table.options.meta as DiscoveryTableMeta;
    return formatFiat(row.original.volByTime, false, false);
  },
});

const renderLiquidityScoreCell = (token: DiscoveryTokenResult, meta: DiscoveryTableMeta) => {
  const skipBlockedTokenCheck = isNeonDbTokensBackedPreset(meta.preset);
  const key = tokenKey(token.token.address, token.token.chainId);

  const score =
    token.liquidityScore != null ? Number(token.liquidityScore) : meta.liquidityScores.get(key);

  const tokenResult = skipBlockedTokenCheck ? undefined : meta.tokenResultsMap.get(key);

  return (
    <LiquidityScoreCell
      item={token}
      score={score}
      scoreLoading={meta.isLiquidityScoresLoading}
      skipBlockedTokenCheck={skipBlockedTokenCheck}
      tokenResult={tokenResult}
    />
  );
};

const liquidityScoreColumn = columnHelper.display({
  size: 100,
  id: 'liquidity_score',
  header: (props) => (
    <HeaderCell
      title={
        <>
          <span className="text-token-gray-600">*</span>Liquidity
        </>
      }
      property="liquidity_score"
      {...props}
    />
  ),
  cell: ({ row, table }) =>
    renderLiquidityScoreCell(row.original, table.options.meta as DiscoveryTableMeta),
});

const ageColumn = columnHelper.accessor('age', {
  size: 80,
  header: (props) => <HeaderCell title="Age" property="age" sortable={true} {...props} />,
  cell: ({ row }) => row.original.age ?? '-',
});

const holdersColumn = columnHelper.accessor('numHolders', {
  size: 200,
  header: (props) => (
    <HeaderCell title="Holders" property="numHolders" sortable={true} {...props} />
  ),
  cell: ({ row }) => <HoldersCell item={row.original} />,
});

const numTransactionsByTimeColumn = columnHelper.accessor('numTxsByTime', {
  size: 100,
  header: (props) => <HeaderCell title="Txns" property="numTxsByTime" sortable={true} {...props} />,
  cell: ({ row }) => shortFormatter.format(row.original.numTxsByTime),
});

const currencyColumn = columnHelper.accessor('currency', {
  size: 100,
  header: (props) => <HeaderCell title="Currency" property="currency" sortable={true} {...props} />,
  cell: ({ row }) => row.original.currency ?? '-',
});

const issuerColumn = columnHelper.accessor('issuer', {
  size: 200,
  header: (props) => <HeaderCell title="Issuer" property="issuer" sortable={true} {...props} />,
  cell: ({ row }) => <IssuerCell item={row.original} />,
});

const backingColumn = (title = 'Backing') =>
  columnHelper.accessor('backing', {
    size: 150,
    header: (props) => <HeaderCell title={title} property="backing" sortable={true} {...props} />,
    cell: ({ row }) => <span className="capitalize">{row.original.backing ?? '-'}</span>,
  });

const DEFAULT_COLUMNS = [
  tokenColumn,
  priceColumn,
  ageColumn,
  numTransactionsByTimeColumn,
  holdersColumn,
  marketCapColumn,
  volumeByTimeColumn,
  liquidityScoreColumn,
  priceChangeColumn('5m'),
  priceChangeColumn('1h'),
  priceChangeColumn('4h'),
  priceChangeColumn('24h'),
];

const POPULAR_COLUMNS = [
  tokenColumn,
  priceColumn,
  priceChangeColumn('24h'),
  priceChangeColumn('7d'),
  priceChangeColumn('30d'),
  volumeByTimeColumn,
  marketCapColumn,
  liquidityScoreColumn,
  holdersColumn,
];

const STOCK_COLUMNS = [
  tokenColumn,
  priceColumn,
  priceChangeColumn('24h'),
  priceChangeColumn('7d'),
  priceChangeColumn('30d'),
  volumeByTimeColumn,
  marketCapColumn,
  liquidityScoreColumn,
  backingColumn('Underlying Asset'),
  issuerColumn,
  holdersColumn,
];

// Unified columns for supporting Asset and DiscoveryTokenResult mixed rows
/**
 * Simple header component for unified token columns (no sorting)
 */
function UnifiedHeaderCell({ title }: { title: string | React.ReactNode }) {
  return (
    <span className={fontStyles({ variant: 'LABEL_SM', class: ['text-token-gray-800'] })}>
      {title}
    </span>
  );
}

const unifiedTokenColumn = unifiedColumnHelper.display({
  size: 320,
  header: 'Token',
  id: 'token',
  cell: ({ row }) => {
    if (isAsset(row.original)) {
      return <UnifiedTokenNameCell asset={row.original} />;
    }
    // Standalone token
    return <TokenNameCell item={row.original as DiscoveryTokenResult} />;
  },
});

const unifiedPriceColumn = unifiedColumnHelper.display({
  size: 150,
  header: (props) => <HeaderCell title="Price" property="price" sortable={true} {...props} />,
  id: 'price',
  cell: ({ row, table }) => {
    const price = isAsset(row.original)
      ? row.original.price
      : parseFloat((row.original as DiscoveryTokenResult).price);

    if (price === undefined || price === null || isNaN(price)) {
      return '-';
    }
    const { currencySymbol, convertFromUsd } = table.options.meta as DiscoveryTableMeta;
    return (
      <SubscriptNotation
        className={fontStyles({ variant: 'BODY_MD' })}
        number={new Decimal(convertFromUsd(price))}
        symbol={currencySymbol}
      />
    );
  },
});

const unifiedPriceChangeColumn = (timeframe: '1h' | '24h' | '7d' | '30d') =>
  unifiedColumnHelper.display({
    size: 100,
    id: `change${timeframe}`,
    header: () => <UnifiedHeaderCell title={`${timeframe} %`} />,
    cell: ({ row }) => {
      let change: number | string | null | undefined;

      if (isAsset(row.original)) {
        change = row.original[`change${timeframe}`];
      } else {
        const token = row.original as DiscoveryTokenResult;
        change = token[`change${timeframe}`];
      }

      if (change === undefined || change === null) {
        return '-';
      }
      return <PriceChangeCell priceChange={String(change)} />;
    },
  });

const unifiedMarketCapColumn = unifiedColumnHelper.display({
  size: 150,
  id: 'marketCap',
  header: (props) => (
    <HeaderCell title="Market Cap" property="marketCap" sortable={true} {...props} />
  ),
  cell: ({ row, table }) => {
    const rawMarketCap = isAsset(row.original)
      ? row.original.marketCap
      : (row.original as DiscoveryTokenResult).marketCap;

    // Handle null, undefined, empty string, or '0' string
    if (
      rawMarketCap === null ||
      rawMarketCap === undefined ||
      rawMarketCap === '' ||
      rawMarketCap === '0'
    ) {
      return '-';
    }

    const marketCap = typeof rawMarketCap === 'string' ? parseFloat(rawMarketCap) : rawMarketCap;
    if (isNaN(marketCap) || marketCap === 0) {
      return '-';
    }
    const { formatFiat } = table.options.meta as DiscoveryTableMeta;
    return formatFiat(marketCap, false, false);
  },
});

const unifiedVolumeColumn = unifiedColumnHelper.display({
  size: 120,
  id: 'volByTime',
  header: (props) => {
    const { timeframe } = props.table.options.meta as DiscoveryTableMeta;
    const title = `Volume (${formatTimeframeLabel(timeframe)})`;

    return <HeaderCell title={title} property="volByTime" sortable={true} {...props} />;
  },
  cell: ({ row, table }) => {
    const volume = isAsset(row.original)
      ? row.original.volume
      : parseFloat((row.original as DiscoveryTokenResult).volByTime);

    if (volume === undefined || volume === null || isNaN(volume)) {
      return '-';
    }
    const { formatFiat } = table.options.meta as DiscoveryTableMeta;
    return formatFiat(volume, false, false);
  },
});

const unifiedHoldersColumn = unifiedColumnHelper.display({
  size: 100,
  id: 'holders',
  header: () => <UnifiedHeaderCell title="Holders" />,
  cell: ({ row }) => {
    if (isAsset(row.original)) {
      // Sum up holders across all tokens in the asset
      if (!row.original.tokens || row.original.tokens.length === 0) {
        return '-';
      }
      const totalHolders = row.original.tokens.reduce(
        (sum, token) => sum + parseInt(token.numHolders, 10),
        0,
      );
      return shortFormatter.format(totalHolders);
    }
    // Standalone token - use HoldersCell component
    return <HoldersCell item={row.original as DiscoveryTokenResult} />;
  },
});

const unifiedLiquidityScoreColumn = unifiedColumnHelper.display({
  size: 100,
  id: 'liquidity_score',
  header: () => (
    <UnifiedHeaderCell
      title={
        <>
          <span className="text-token-gray-600">*</span>Liquidity
        </>
      }
    />
  ),
  cell: ({ row, table }) => {
    const meta = table.options.meta as DiscoveryTableMeta;
    if (isAsset(row.original)) {
      if (!row.original.tokens || row.original.tokens.length === 0) {
        return '-';
      }
      return <UnifiedLiquidityScoreCell score={row.original.maxLiquidityScore ?? undefined} />;
    }
    return renderLiquidityScoreCell(row.original as DiscoveryTokenResult, meta);
  },
});

const unifiedCurrencyColumn = unifiedColumnHelper.display({
  size: 100,
  id: 'currency',
  header: (props) => <HeaderCell title="Currency" property="currency" sortable={true} {...props} />,
  cell: ({ row }) => {
    if (isAsset(row.original)) {
      return row.original.tokens[0]?.currency ?? '-';
    }
    return (row.original as DiscoveryTokenResult).currency ?? '-';
  },
});

const unifiedIssuerColumn = unifiedColumnHelper.display({
  size: 200,
  id: 'issuer',
  header: (props) => <HeaderCell title="Issuer" property="issuer" sortable={true} {...props} />,
  cell: ({ row }) => {
    if (isAsset(row.original)) {
      const issuer = row.original.tokens[0]?.issuer;
      if (!issuer) {
        return '-';
      }
      return <IssuerCell item={row.original.tokens[0]} />;
    }
    return <IssuerCell item={row.original as DiscoveryTokenResult} />;
  },
});

const unifiedBackingColumn = unifiedColumnHelper.display({
  size: 150,
  id: 'backing',
  header: (props) => (
    <HeaderCell title="Underlying Asset" property="backing" sortable={true} {...props} />
  ),
  cell: ({ row }) => {
    if (isAsset(row.original)) {
      return <span className="capitalize">{row.original.tokens[0]?.backing ?? '-'}</span>;
    }
    return (
      <span className="capitalize">{(row.original as DiscoveryTokenResult).backing ?? '-'}</span>
    );
  },
});

export const CATEGORY_COLUMNS_MAP: Record<DiscoveryPreset, typeof DEFAULT_COLUMNS> = {
  popular: POPULAR_COLUMNS,
  trending: DEFAULT_COLUMNS,
  currencies: [
    tokenColumn,
    priceColumn,
    marketCapColumn,
    volumeByTimeColumn,
    liquidityScoreColumn,
    currencyColumn,
    issuerColumn,
    backingColumn(),
    holdersColumn,
  ],
  lsts: POPULAR_COLUMNS,
  wrapped: POPULAR_COLUMNS,
  commodities: [
    tokenColumn,
    priceColumn,
    priceChangeColumn('24h'),
    priceChangeColumn('7d'),
    priceChangeColumn('30d'),
    volumeByTimeColumn,
    marketCapColumn,
    liquidityScoreColumn,
    issuerColumn,
    backingColumn('Underlying Asset'),
    holdersColumn,
  ],
  stocks: STOCK_COLUMNS,
  social: DEFAULT_COLUMNS,
  launchpads: DEFAULT_COLUMNS,
};

export const UNIFIED_POPULAR_COLUMNS = [
  unifiedTokenColumn,
  unifiedPriceColumn,
  unifiedPriceChangeColumn('24h'),
  unifiedPriceChangeColumn('7d'),
  unifiedPriceChangeColumn('30d'),
  unifiedVolumeColumn,
  unifiedMarketCapColumn,
  unifiedLiquidityScoreColumn,
  unifiedHoldersColumn,
];

export const UNIFIED_COMMODITIES_COLUMNS = [
  unifiedTokenColumn,
  unifiedPriceColumn,
  unifiedPriceChangeColumn('24h'),
  unifiedPriceChangeColumn('7d'),
  unifiedPriceChangeColumn('30d'),
  unifiedVolumeColumn,
  unifiedMarketCapColumn,
  unifiedLiquidityScoreColumn,
  unifiedIssuerColumn,
  unifiedBackingColumn,
  unifiedHoldersColumn,
];

export const UNIFIED_STOCKS_COLUMNS = [
  unifiedTokenColumn,
  unifiedPriceColumn,
  unifiedPriceChangeColumn('24h'),
  unifiedPriceChangeColumn('7d'),
  unifiedPriceChangeColumn('30d'),
  unifiedVolumeColumn,
  unifiedMarketCapColumn,
  unifiedLiquidityScoreColumn,
  unifiedBackingColumn,
  unifiedIssuerColumn,
  unifiedHoldersColumn,
];

export const UNIFIED_CURRENCIES_COLUMNS = [
  unifiedTokenColumn,
  unifiedPriceColumn,
  unifiedMarketCapColumn,
  unifiedVolumeColumn,
  unifiedLiquidityScoreColumn,
  unifiedCurrencyColumn,
  unifiedIssuerColumn,
  unifiedBackingColumn,
  unifiedHoldersColumn,
];
